import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';
import {
  differenceInCalendarDays,
  format,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { Select } from '../../../../components/Select';
import { Pagination } from '../../../../components/Pagination';
import { SearchField } from '../../../../components/SearchField';
import {
  EmptyState,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../../components/Table';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  BulkActionsRow,
  BulkButton,
  CheckboxLabel,
  Container,
  FiltersRow,
  HeaderRow,
  LockedState,
  MarkPaidButton,
  NotifyButton,
  PageSubtitle,
  PageTitle,
  PaidLabel,
  Row,
  SelectWrapper,
  StatusBadge,
  StudentAvatar,
  StudentInfo,
  StudentName,
} from './styles';

interface IGymDueRow {
  user_id: string;
  fullname: string;
  avatar_url?: string;
  amount: number;
  due_day: number;
  due_date: string;
  status: 'pending' | 'overdue' | 'ok';
  days_overdue?: number;
  paid_at?: string;
}

type RowTone = 'danger' | 'warning' | 'paid' | 'none';

const PAGE_SIZE = 10;

function sortDuesRows(a: IGymDueRow, b: IGymDueRow) {
  const rankA = a.status === 'ok' ? 1 : 0;
  const rankB = b.status === 'ok' ? 1 : 0;

  if (rankA !== rankB) return rankA - rankB;

  const dueDiff =
    new Date(a.due_date).getTime() - new Date(b.due_date).getTime();

  if (dueDiff !== 0) return dueDiff;

  return a.fullname.localeCompare(b.fullname);
}

const OverdueStudents: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [students, setStudents] = useState<IGymDueRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [reminderDaysBefore, setReminderDaysBefore] = useState(3);
  const [autoReminderEnabled, setAutoReminderEnabled] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [competence, setCompetence] = useState(() =>
    format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  );
  const [showPaid, setShowPaid] = useState(false);

  const [notifying, setNotifying] = useState(false);
  const [markingPaid, setMarkingPaid] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkNotifying, setBulkNotifying] = useState(false);
  const [bulkPaying, setBulkPaying] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [debouncedSearch, competence, showPaid]);

  const fetchDues = useCallback(() => {
    setLoading(true);

    api
      .get('dues/gym/dues', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          competence_date: competence,
          show_paid: showPaid,
        },
      })
      .then(response => {
        setStudents(response.data.data);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
        setReminderDaysBefore(response.data.reminder_days_before);
        setAutoReminderEnabled(response.data.auto_reminder_enabled);
      })
      .catch(err => {
        if (err?.response?.status === 403) {
          setLocked(true);
        }
      })
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, competence, showPaid]);

  useEffect(() => {
    fetchDues();
  }, [fetchDues]);

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }).map((_, index) => {
      const date = startOfMonth(subMonths(new Date(), index));
      const value = format(date, 'yyyy-MM-dd');
      const label = format(date, "MMMM 'de' yyyy", { locale: ptBR });

      return { value, label: label.charAt(0).toUpperCase() + label.slice(1) };
    });
  }, []);

  const handleChangeCompetence = useCallback((option: any) => {
    setCompetence(option.value);
  }, []);

  const confirmNotifyIfAutoReminderActive = useCallback(() => {
    if (!autoReminderEnabled) return true;

    // eslint-disable-next-line no-alert
    return window.confirm(
      `A notificação automática já está ativa para ${reminderDaysBefore} dia(s) antes do vencimento. Deseja notificar manualmente mesmo assim?`,
    );
  }, [autoReminderEnabled, reminderDaysBefore]);

  const handleNotify = useCallback(() => {
    if (!confirmNotifyIfAutoReminderActive()) return;

    setNotifying(true);

    api
      .post('dues/gym/overdue/notify')
      .then(response => {
        addToast({
          type: 'success',
          title: 'Push enviado',
          description: `${response.data.notified_count} aluno(s) notificado(s).`,
        });
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao notificar',
          description: 'Tente novamente mais tarde.',
        });
      })
      .finally(() => setNotifying(false));
  }, [addToast, confirmNotifyIfAutoReminderActive]);

  const handleNotifySelected = useCallback(() => {
    if (!selectedIds.size || !confirmNotifyIfAutoReminderActive()) return;

    setBulkNotifying(true);

    api
      .post('dues/gym/notify', { user_ids: Array.from(selectedIds) })
      .then(response => {
        addToast({
          type: 'success',
          title: 'Push enviado',
          description: `${response.data.notified_count} aluno(s) notificado(s).`,
        });
        setSelectedIds(new Set());
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao notificar',
          description: 'Tente novamente mais tarde.',
        });
      })
      .finally(() => setBulkNotifying(false));
  }, [addToast, confirmNotifyIfAutoReminderActive, selectedIds]);

  const applyOptimisticPaid = useCallback(
    (userId: string) => {
      setStudents(current => {
        const updated = current.map(student =>
          student.user_id === userId
            ? {
                ...student,
                status: 'ok' as const,
                paid_at: new Date().toISOString(),
                days_overdue: undefined,
              }
            : student,
        );

        const visible = updated.filter(
          student => showPaid || student.status !== 'ok',
        );

        return [...visible].sort(sortDuesRows);
      });

      if (!showPaid) {
        setTotal(current => Math.max(0, current - 1));
      }
    },
    [showPaid],
  );

  const handleMarkPaid = useCallback(
    (userId: string) => {
      setMarkingPaid(userId);

      api
        .post(`dues/${userId}/payments`, { competence_date: competence })
        .then(() => {
          applyOptimisticPaid(userId);
          addToast({ type: 'success', title: 'Pagamento registrado.' });
          setSelectedIds(current => {
            if (!current.has(userId)) return current;
            const next = new Set(current);
            next.delete(userId);
            return next;
          });
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: 'Erro ao registrar pagamento.',
            description: 'Tente novamente mais tarde.',
          });
        })
        .finally(() => setMarkingPaid(undefined));
    },
    [addToast, applyOptimisticPaid, competence],
  );

  const handleMarkPaidSelected = useCallback(async () => {
    if (!selectedIds.size) return;

    setBulkPaying(true);

    const ids = Array.from(selectedIds);

    const results = await Promise.allSettled(
      ids.map(userId =>
        api.post(`dues/${userId}/payments`, { competence_date: competence }),
      ),
    );

    let successCount = 0;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        applyOptimisticPaid(ids[index]);
        successCount += 1;
      }
    });

    setSelectedIds(new Set());
    setBulkPaying(false);

    if (successCount) {
      addToast({
        type: 'success',
        title: `${successCount} pagamento(s) registrado(s).`,
      });
    }

    if (successCount < ids.length) {
      addToast({
        type: 'error',
        title: 'Alguns pagamentos não puderam ser registrados.',
        description: 'Tente novamente para os que falharam.',
      });
    }
  }, [addToast, applyOptimisticPaid, competence, selectedIds]);

  const rowTone = useCallback(
    (student: IGymDueRow): RowTone => {
      if (student.status === 'ok') return 'paid';
      if (student.status === 'overdue') return 'danger';

      const daysUntilDue = differenceInCalendarDays(
        parseISO(student.due_date),
        new Date(),
      );

      return daysUntilDue <= reminderDaysBefore ? 'warning' : 'none';
    },
    [reminderDaysBefore],
  );

  const isSelectable = useCallback(
    (student: IGymDueRow) => {
      const tone = rowTone(student);
      return tone === 'danger' || tone === 'warning';
    },
    [rowTone],
  );

  const toggleSelected = useCallback((userId: string) => {
    setSelectedIds(current => {
      const next = new Set(current);

      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }

      return next;
    });
  }, []);

  const statusLabel = useCallback(
    (student: IGymDueRow) => {
      if (student.status === 'ok') {
        return `Pago em ${format(
          parseISO(student.paid_at as string),
          'dd/MM/yyyy',
        )}`;
      }

      if (student.status === 'overdue') {
        return `Vencido há ${student.days_overdue}d`;
      }

      return rowTone(student) === 'warning' ? 'Vencendo' : 'Pendente';
    },
    [rowTone],
  );

  return (
    <Painel loading={loading}>
      <Container>
        <HeaderRow>
          <div>
            <PageTitle>Inadimplência</PageTitle>
            <PageSubtitle>
              Pagamentos pendentes, vencendo e vencidos dos alunos
            </PageSubtitle>
          </div>

          {!locked && (
            <NotifyButton
              type="button"
              onClick={handleNotify}
              disabled={notifying}
            >
              {notifying ? 'Enviando...' : 'Notificar todos vencidos'}
            </NotifyButton>
          )}
        </HeaderRow>

        {locked && (
          <LockedState>
            <Icon
              name="notifications_active"
              size={40}
              color={colors.onSurfaceVariant}
            />
            <h2>Recurso não disponível no momento</h2>
            <PageSubtitle>
              Fale com o suporte pra saber mais sobre esse recurso.
            </PageSubtitle>
          </LockedState>
        )}

        {!locked && (
          <>
            <FiltersRow>
              <SearchField
                placeholder="Buscar por nome..."
                value={searchText}
                onChange={setSearchText}
              />

              <Form onSubmit={() => undefined}>
                <SelectWrapper>
                  <Select
                    name="competence"
                    options={monthOptions}
                    defaultValue={monthOptions[0]}
                    onChange={handleChangeCompetence}
                    blurInputOnSelect
                  />
                </SelectWrapper>
              </Form>

              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={showPaid}
                  onChange={e => setShowPaid(e.target.checked)}
                />
                Exibir pagos
              </CheckboxLabel>
            </FiltersRow>

            <BulkActionsRow>
              <span>{selectedIds.size} selecionado(s)</span>
              <BulkButton
                type="button"
                disabled={!selectedIds.size || bulkNotifying}
                onClick={handleNotifySelected}
              >
                {bulkNotifying ? 'Enviando...' : 'Notificar selecionados'}
              </BulkButton>
              <BulkButton
                type="button"
                disabled={!selectedIds.size || bulkPaying}
                onClick={handleMarkPaidSelected}
              >
                {bulkPaying ? 'Registrando...' : 'Confirmar pagamento'}
              </BulkButton>
            </BulkActionsRow>

            <TableWrapper>
              <Table>
                <Thead>
                  <tr>
                    <Th />
                    <Th>Aluno</Th>
                    <Th>Vencimento</Th>
                    <Th>Valor</Th>
                    <Th>Status</Th>
                    <Th>Ações</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {students.map(student => (
                    <Row key={student.user_id} tone={rowTone(student)}>
                      <Td>
                        {isSelectable(student) && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(student.user_id)}
                            onChange={() => toggleSelected(student.user_id)}
                          />
                        )}
                      </Td>
                      <Td
                        onClick={() =>
                          navigate(`/users/${student.user_id}/dues`)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <StudentInfo>
                          <StudentAvatar
                            src={student.avatar_url || noAvatar}
                            alt={student.fullname}
                          />
                          <StudentName paid={student.status === 'ok'}>
                            {formatName(student.fullname)}
                          </StudentName>
                        </StudentInfo>
                      </Td>
                      <Td>
                        {format(parseISO(student.due_date), 'dd/MM/yyyy')}
                      </Td>
                      <Td>R$ {student.amount.toFixed(2)}</Td>
                      <Td>
                        <StatusBadge tone={rowTone(student)}>
                          {statusLabel(student)}
                        </StatusBadge>
                      </Td>
                      <Td>
                        {student.status === 'ok' ? (
                          <PaidLabel>Pagamento registrado</PaidLabel>
                        ) : (
                          <MarkPaidButton
                            type="button"
                            disabled={markingPaid === student.user_id}
                            onClick={() => handleMarkPaid(student.user_id)}
                          >
                            {markingPaid === student.user_id
                              ? 'Registrando...'
                              : 'Marcar pagamento'}
                          </MarkPaidButton>
                        )}
                      </Td>
                    </Row>
                  ))}
                </Tbody>
              </Table>

              {!students.length && (
                <EmptyState>Nenhum pagamento encontrado.</EmptyState>
              )}
            </TableWrapper>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={PAGE_SIZE}
              itemLabel="pagamentos"
              onPageChange={setPage}
            />
          </>
        )}
      </Container>
    </Painel>
  );
};

export { OverdueStudents };
